/**
 * Lokalt dokumentregister för Centralspec. Separat nyckelprefix så att det
 * inte krockar med schema-editorns register i samma browser.
 */

const STORAGE_KEY = "electro-planner-panels:documents";
const MAX_ENTRIES = 200;
export const DEFAULT_DOCUMENT_ID = "local";
export const INDEXEDDB_PREFIX = "electro-planner-panels";

export type DocumentRecord = {
  id: string;
  title: string;
  createdAt: string;
  lastEditedAt: string;
};

function safeParse(raw: string | null): DocumentRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is DocumentRecord =>
        typeof r?.id === "string" &&
        typeof r?.title === "string" &&
        typeof r?.createdAt === "string" &&
        typeof r?.lastEditedAt === "string",
    );
  } catch {
    return [];
  }
}

function readAll(): DocumentRecord[] {
  if (typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

function writeAll(records: DocumentRecord[]): void {
  if (typeof window === "undefined") return;
  const trimmed = records.slice(0, MAX_ENTRIES);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // ignore full localStorage
  }
}

export function listDocuments(): DocumentRecord[] {
  return readAll().sort((a, b) => b.lastEditedAt.localeCompare(a.lastEditedAt));
}

export function touchDocument(
  id: string,
  patch: Partial<Pick<DocumentRecord, "title">> = {},
): DocumentRecord {
  const now = new Date().toISOString();
  const records = readAll();
  const idx = records.findIndex((r) => r.id === id);
  if (idx >= 0) {
    const existing = records[idx]!;
    const updated: DocumentRecord = { ...existing, ...patch, lastEditedAt: now };
    records[idx] = updated;
    writeAll(records);
    return updated;
  }
  const created: DocumentRecord = {
    id,
    title: patch.title ?? "",
    createdAt: now,
    lastEditedAt: now,
  };
  records.push(created);
  writeAll(records);
  return created;
}

export function forgetDocument(id: string): void {
  writeAll(readAll().filter((r) => r.id !== id));
}

export function createDocumentId(): string {
  const random = Math.random().toString(36).slice(2, 8);
  return `panel-${Date.now().toString(36)}-${random}`;
}
